import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  RowActionMenu,
  type RowAction,
} from '@backrs_ui/grow-ui/src/components/RowActionMenu/RowActionMenu';
import {
  institutionLabel,
  offeringLabel,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import {
  useGetRosterCodesQuery,
  type AdminCode,
  type AdminRoster,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import {
  Box,
  Chip,
  type SxProps,
  TableCell,
  TableRow,
  type Theme,
  Typography,
} from '@mui/material';

import { errorTagSx } from './errorTag';
import { LineCode, lineCodeState } from './LineCode';
import { activeCodeForLine, rosterLineKey } from './rosterCodes';

const { rosters: rosterStrings } = appStrings.modules.coachingDashboard.institutionAdmin;
const strings = appStrings.modules.rostersAdmin;

export type LineDoors = Readonly<{
  /** True only for the line whose own mint is in flight; one mutation instance serves them all. */
  isCreatingFor: (lineKey: string) => boolean;
  onCreateCode: (roster: AdminRoster) => void;
  onRevoke: (roster: AdminRoster, code: AdminCode) => void;
}>;

type RosterRowProps = Readonly<{
  roster: AdminRoster;
  doors: LineDoors;
}>;

/** Its own codes read: the merged roster row carries no code, and the line needs the one it owns. */
export const RosterRow = ({ roster, doors }: RosterRowProps) => {
  const codes = useGetRosterCodesQuery({ rosterId: roster.id });
  const name = offeringLabel(roster.offering);
  const activeCode = activeCodeForLine(codes.data?.data ?? [], null);
  const codeState = lineCodeState(
    activeCode,
    codes.isFetching,
    codes.isError,
    // A partial decode failure is not an error on this read — contextualList only errors when
    // EVERY row fails — so completeness has to travel separately from isError.
    (codes.data?.dropped ?? 0) === 0,
  );

  const isCreating = doors.isCreatingFor(rosterLineKey(roster.id));

  const actions: RowAction[] = [];

  if (activeCode !== null && codeState === 'present') {
    const code = activeCode;

    actions.push({
      id: `rosters-admin-roster-revoke-${roster.id}`,
      label: strings.actions.revoke,
      onSelect: () => doors.onRevoke(roster, code),
    });
  }

  return (
    <TableRow id={`dashboard-roster-row-${roster.id}`} sx={rowSx}>
      <TableCell>
        <Typography variant="text-sm-medium">{name}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="text-sm-regular">
          {institutionLabel(roster.managingInstitution)}
        </Typography>
      </TableCell>
      <TableCell>
        <Box sx={capacityCellSx}>
          <Typography id={`rosters-admin-roster-seats-${roster.id}`} variant="text-sm-medium">
            {strings.capacity.usage(roster.seatsUsed, roster.capacity)}
          </Typography>
          {roster.isOverCap ? (
            <Chip
              id={`dashboard-roster-over-cap-${roster.id}`}
              label={rosterStrings.overCap}
              variant="tag"
              size="sm"
              sx={errorTagSx}
            />
          ) : atCapacity(roster) ? (
            <Chip
              id={`rosters-admin-roster-at-cap-${roster.id}`}
              label={strings.capacity.atCapacity}
              variant="tag"
              size="sm"
            />
          ) : null}
        </Box>
      </TableCell>
      <TableCell>
        <LineCode
          id={`rosters-admin-roster-code-${roster.id}`}
          code={activeCode}
          state={codeState}
          isCreating={isCreating}
          onCreate={() => doors.onCreateCode(roster)}
        />
      </TableCell>
      <TableCell>
        {actions.length > 0 ? (
          <RowActionMenu
            id={`rosters-admin-roster-actions-${roster.id}`}
            ariaLabel={strings.actions.rosterAria(name)}
            actions={actions}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
};

const atCapacity = (roster: AdminRoster): boolean =>
  roster.capacity !== null && roster.seatsUsed >= roster.capacity;

export const ROSTER_TABLE_ARIA = rosterStrings.title;

export const ROSTER_HEADERS = [
  rosterStrings.columns.roster,
  rosterStrings.columns.institution,
  strings.columnsV2.seats,
  strings.columnsV2.code,
  strings.columns.actions,
];

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiTableCell-root': {
    paddingBlock: backrs.spacing['spacing-xl'],
  },
});

const capacityCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});
